import { useCallback, useEffect, useRef, useState } from 'react';
import { getRacePlaybackMetadata, getRacePlaybackFrame, getRacePlaybackStreamUrl, getRacePlaybackPilotToken } from './api';

// A race has three states: unresolved metadata, explicitly live, or pub.
// Never render raw live values while the server's setting is unresolved.
export default function useRacePlayback(raceId, { followPub = true, subscribe = true } = {}) {
  const [settings, setSettings] = useState(null);
  const [override, setOverride] = useState(null);
  const [frameRecord, setFrameRecord] = useState(null);
  const [pilotToken, setPilotToken] = useState(null);
  const previouslySyncedRace = useRef(null);
  const [transport, setTransport] = useState({ raceId: null, status: 'warming' });
  const currentSettings = settings?.forRaceId === String(raceId) ? settings : null;
  const selectedOverride = override?.raceId === String(raceId) ? override.mode : null;
  const explicitlyLive = currentSettings?.available === true && currentSettings.enabled === false &&
    Number.isSafeInteger(currentSettings.version) && currentSettings.version > 0;
  const mode = !raceId || !currentSettings ? 'pending' :
    currentSettings.available && currentSettings.enabled
      ? (currentSettings.requiresViewerToken || (selectedOverride || (followPub ? 'pub' : 'live')) === 'pub') ? 'pub' : 'live'
      : explicitlyLive ? 'live' : previouslySyncedRace.current === String(raceId) ? 'pending' : 'live';
  // Once this screen has viewed a delayed race, a gate/settings failure may
  // never reinterpret it as raw live data. A versioned organiser disable is explicit.
  if (mode === 'pub') previouslySyncedRace.current = String(raceId);
  const settingsVersion = currentSettings?.version;
  const delayMs = currentSettings?.delayMs;
  const streamKey = `${raceId}:${mode}:${settingsVersion}:${delayMs}:${currentSettings?.requiresViewerToken ? 'pilot' : 'dev'}`;
  const tokenForRace = pilotToken?.raceId === String(raceId) && pilotToken?.version === settingsVersion
    && pilotToken?.expiresAt > Date.now() + 10000 ? pilotToken.token : null;

  useEffect(() => {
    if (!raceId || mode !== 'pub' || !subscribe || !currentSettings?.requiresViewerToken) return undefined;
    let disposed = false;
    let timer;
    let retryMs = 5000;
    const load = async () => {
      let nextMs = 5 * 60 * 1000;
      try {
        const next = await getRacePlaybackPilotToken(raceId);
        if (disposed) return;
        setPilotToken({ ...next, raceId: String(raceId), version: settingsVersion });
        retryMs = 5000;
      } catch (error) {
        if (disposed) return;
        // A transport failure does not revoke an existing valid credential.
        // Expiry/race/version checks above still prevent using invalid tokens.
        const denied = [401, 403].includes(error.response?.status);
        if (denied) setPilotToken(null);
        nextMs = denied ? 30000 : retryMs;
        retryMs = Math.min(retryMs * 2, 30000);
      }
      if (!disposed) timer = setTimeout(load, nextMs);
    };
    load();
    return () => { disposed = true; clearTimeout(timer); };
  }, [raceId, mode, subscribe, currentSettings?.requiresViewerToken, settingsVersion]);

  useEffect(() => {
    if (!raceId) return undefined;
    let disposed = false;
    let requestNumber = 0;
    let acceptedNumber = 0;
    const load = async () => {
      const number = ++requestNumber;
      try {
        const response = await getRacePlaybackMetadata(raceId);
        if (disposed || number < acceptedNumber) return;
        acceptedNumber = number;
        setSettings(previous => {
          const existing = previous?.forRaceId === String(raceId) ? previous : null;
          if (existing && Number.isFinite(existing.version) && Number.isFinite(response.version) &&
            response.version < existing.version) return existing;
          return { ...response, forRaceId: String(raceId) };
        });
      } catch {
        // On first load, pending persists. A failed refresh cannot reinterpret
        // a known pub setting as live or reset a newer playback frame.
        if (!disposed && number >= acceptedNumber) setTransport({ raceId: String(raceId), status: 'metadata_unavailable' });
      }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { disposed = true; clearInterval(timer); };
  }, [raceId]);

  useEffect(() => {
    setFrameRecord(null);
    setTransport({ raceId: String(raceId), status: mode === 'live' ? 'live' : 'warming' });
    if (!raceId || mode !== 'pub' || !subscribe ||
        (currentSettings?.requiresViewerToken && !tokenForRace)) return undefined;
    let disposed = false, lastActivity = 0, pending = false;
    let stream = null;
    let current = null;
    let latestSuccessAt = 0;
    let lastSseAt = 0;
    let eventSequence = 0;
    const accept = (data, origin, startedAt) => {
      if (disposed) return;
      // Never let a pre-SSE fallback failure/data/status invalidate a newer SSE frame.
      if (origin === 'http' && startedAt < lastSseAt) return;
      if (!data?.ok) {
        setFrameRecord(null);
        setTransport({ raceId: String(raceId), status: data?.status || 'warming' });
        return;
      }
      if (data.settingsVersion !== settingsVersion || data.delayMs !== delayMs ||
          String(data.race?.id) !== String(raceId)) return;
      const epoch = String(data.epoch);
      if (current) {
        if (current.epoch === epoch) {
          if (data.targetMs < current.targetMs ||
              (data.targetMs === current.targetMs && data.frameVersion < current.frameVersion)) return;
        } else if (origin !== 'sse' && Date.now() - lastSseAt < 5000) {
          // A fallback from a retired epoch must not displace the SSE timeline.
          return;
        }
      }
      current = { epoch, targetMs: data.targetMs, frameVersion: data.frameVersion };
      latestSuccessAt = Date.now();
      setFrameRecord({ key: streamKey, data });
      setTransport({ raceId: String(raceId), status: data.status });
      lastActivity = Date.now();
    };
    if (typeof EventSource !== 'undefined') {
      stream = new EventSource(getRacePlaybackStreamUrl(raceId, tokenForRace));
      stream.onmessage = e => {
        try { const data = JSON.parse(e.data); ++eventSequence; lastSseAt = Date.now(); accept(data, 'sse', lastSseAt); }
        catch { /* Ignore malformed packet */ }
      };
      stream.addEventListener('status', e => {
        if (disposed) return;
        ++eventSequence;
        try {
          const event = JSON.parse(e.data);
          setFrameRecord(null);
          setTransport({ raceId: String(raceId), status: event.status || 'transport_failure' });
        } catch { setFrameRecord(null); setTransport({ raceId: String(raceId), status: 'transport_failure' }); }
      });
      stream.addEventListener('access-expired', () => {
        if (disposed) return;
        ++eventSequence;
        setFrameRecord(null); setTransport({ raceId: String(raceId), status: 'unavailable' }); stream?.close();
      });
      stream.onerror = () => {
        if (disposed) return;
        ++eventSequence;
        setFrameRecord(null); setTransport({ raceId: String(raceId), status: 'transport_failure' });
      };
    }
    const fallback = async () => {
      if (disposed || pending || Date.now() - lastActivity < 4000) return;
      pending = true;
      const startedAt = Date.now();
      const sequenceAtStart = eventSequence;
      try {
        const data = await getRacePlaybackFrame(raceId, tokenForRace);
        if (sequenceAtStart === eventSequence) accept(data, 'http', startedAt);
      }
      catch (e) {
        if (!disposed && sequenceAtStart === eventSequence && startedAt >= lastSseAt && (!latestSuccessAt || startedAt >= latestSuccessAt)) {
          setFrameRecord(null);
          setTransport({ raceId: String(raceId), status: e.response?.data?.status || 'transport_failure' });
        }
      } finally { pending = false; }
    };
    const timer = setInterval(fallback, 1500);
    fallback();
    return () => { disposed = true; stream?.close(); clearInterval(timer); };
  }, [streamKey, raceId, mode, subscribe, settingsVersion, delayMs, currentSettings?.requiresViewerToken, tokenForRace]);

  const chooseMode = useCallback(next => setOverride({ raceId: String(raceId), mode: next === 'pub' ? 'pub' : 'live' }), [raceId]);
  const frame = mode === 'pub' && (!currentSettings?.requiresViewerToken || !!tokenForRace) &&
    frameRecord?.key === streamKey &&
    frameRecord.data.settingsVersion === settingsVersion && frameRecord.data.delayMs === delayMs &&
    String(frameRecord.data.race?.id) === String(raceId) ? frameRecord.data : null;
  const status = mode === 'pub' && currentSettings?.requiresViewerToken && !tokenForRace ? 'access_required' :
    mode === 'pending' ? (transport.raceId === String(raceId) && transport.status === 'metadata_unavailable'
    ? 'metadata_unavailable' : 'warming') : mode === 'live' ? 'live' :
    transport.raceId === String(raceId) ? transport.status : 'warming';
  return { mode, chooseMode, settings: currentSettings, frame, status };
}
